/**
 * Client-side SEO helper — sets document.title, meta tags, OG tags, canonical,
 * and injects JSON-LD structured data. Uses vanilla DOM (no react-helmet).
 *
 * Usage:
 *   <SEO
 *     path="/produk"
 *     pageName="Produk"                     // fills {page} in the title_template
 *     description="Optional override"       // overrides seo-page from admin
 *     ogImage="https://.../img.jpg"
 *     jsonLd={[schemaOrg]}
 *   />
 *
 * Admin can further override via GET /api/seo/page?path=... .
 */
import { useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const OWNED_ATTR = 'data-seo-owned';

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector + `[${OWNED_ATTR}]`);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute(OWNED_ATTR, '1');
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"][${OWNED_ATTR}]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(OWNED_ATTR, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
};

const clearJsonLd = () => {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${OWNED_ATTR}]`)
    .forEach((n) => n.remove());
};

const injectJsonLd = (blobs) => {
  clearJsonLd();
  blobs.filter(Boolean).forEach((blob) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute(OWNED_ATTR, '1');
    s.textContent = JSON.stringify(blob);
    document.head.appendChild(s);
  });
};

const formatTitle = (tpl, pageName) => {
  const t = (tpl || '').trim();
  if (!t || !t.includes('{page}') || !pageName) return '';
  return t.replace('{page}', pageName);
};

// Title precedence:
//   1. Explicit `title` prop (per-component override, e.g. product detail)
//   2. `cfg.title` returned by /api/seo/page  (per-page admin override, else global default_title)
//   3. Formatted title_template with pageName (e.g. "Produk | JILONG Indonesia")
//   4. Bare pageName
//   5. "JILONG Indonesia"
const resolveTitle = ({ title, pageName, cfg }) => {
  if (title) return title;
  if (cfg.title) return cfg.title;
  const templated = formatTitle(cfg.title_template, pageName);
  if (templated) return templated;
  return pageName || 'JILONG Indonesia';
};

const SEO = ({ path, pageName, title, description, ogImage, keywords, canonical, jsonLd = [] }) => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let cfg = {};
      try {
        const r = await axios.get(`${API}/api/seo/page?path=${encodeURIComponent(path || window.location.pathname)}`);
        cfg = r.data || {};
      } catch {
        cfg = {};
      }
      if (cancelled) return;

      const finalTitle = resolveTitle({ title, pageName, cfg });
      const finalDesc = description || cfg.description || '';
      const finalKw = keywords || cfg.keywords || '';
      const finalOg = ogImage || cfg.og_image || '';
      const canon = canonical || cfg.canonical || (cfg.site_url ? `${cfg.site_url.replace(/\/$/, '')}${path || window.location.pathname}` : window.location.href);

      document.title = finalTitle;
      upsertMeta('meta[name="description"]', { name: 'description', content: finalDesc });
      upsertMeta('meta[name="keywords"]', { name: 'keywords', content: finalKw });
      upsertMeta('meta[property="og:title"]', { property: 'og:title', content: finalTitle });
      upsertMeta('meta[property="og:description"]', { property: 'og:description', content: finalDesc });
      upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
      upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canon });
      if (finalOg) upsertMeta('meta[property="og:image"]', { property: 'og:image', content: finalOg });
      upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
      upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: finalTitle });
      upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: finalDesc });
      if (finalOg) upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: finalOg });
      upsertMeta('meta[name="robots"]', { name: 'robots', content: cfg.noindex ? 'noindex, nofollow' : 'index, follow' });
      upsertLink('canonical', canon);

      // JSON-LD blobs: page-specific + a default Organization schema
      const org = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: cfg.site_name || 'JILONG Indonesia',
        url: cfg.site_url || '',
        logo: finalOg || '',
      };
      injectJsonLd([org, ...jsonLd]);
    })();
    return () => { cancelled = true; };
  }, [path, pageName, title, description, ogImage, keywords, canonical, JSON.stringify(jsonLd)]);

  return null;
};

export default SEO;
